import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
    getAllNotifications,
    markNotificationsAsRead,
    markNotificationsAsUnread,
    markAllNotificationsAsRead,
    deleteNotifications as deleteNotificationsApi,
} from '../Services/NotificationService';

const CACHE_KEY_PREFIX = 'aegis_notifications_';
const CACHE_EXPIRY_MS = 60 * 60 * 1000; // getAllNotifications is throttled to this cadence
const PAGE_SIZE = 20;

const mapNotification = (row) => ({
    id: row.Id,
    title: row.Title,
    message: row.Message,
    isRead: row.IsRead,
    timestamp: row.CreatedOn,
});

export function useNotifications(token, userId) {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const cacheKey = userId ? `${CACHE_KEY_PREFIX}${userId}` : null;

    // Persist only after the initial cache/fetch has settled, so this never overwrites a valid cache with the empty initial state.
    useEffect(() => {
        if (!cacheKey || isLoading) return;
        localStorage.setItem(cacheKey, JSON.stringify({ data: notifications, unreadCount, timestamp: Date.now() }));
    }, [cacheKey, notifications, unreadCount, isLoading]);

    useEffect(() => {
        if (!token || !cacheKey) {
            setIsLoading(false);
            return undefined;
        }

        let isMounted = true;

        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            try {
                const { data, unreadCount: cachedUnread, timestamp } = JSON.parse(cached);
                if (Date.now() - timestamp < CACHE_EXPIRY_MS) {
                    setNotifications(data);
                    setUnreadCount(cachedUnread ?? 0);
                    offsetRef.current = data.length;
                    setHasMore(data.length >= PAGE_SIZE);
                    setIsLoading(false);
                    return undefined;
                }
            } catch {
                localStorage.removeItem(cacheKey);
            }
        }

        const fetchNotifications = async () => {
            try {
                setIsLoading(true);
                const response = await getAllNotifications(token, PAGE_SIZE, 0);
                if (!isMounted) return;
                if (response?.is_success) {
                    const list = (response.data?.Notifications || []).map(mapNotification);
                    setNotifications(list);
                    setUnreadCount(response.data?.UnreadCount ?? 0);
                    offsetRef.current = list.length;
                    setHasMore(list.length >= PAGE_SIZE);
                }
            } catch (error) {
                console.error('Failed to fetch notifications', error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchNotifications();

        return () => {
            isMounted = false;
        };
    }, [token, cacheKey]);

    const loadMore = useCallback(async () => {
        if (!token || isLoadingMore || !hasMore) return;
        try {
            setIsLoadingMore(true);
            const response = await getAllNotifications(token, PAGE_SIZE, offsetRef.current);
            if (response?.is_success) {
                const list = (response.data?.Notifications || []).map(mapNotification);
                offsetRef.current += list.length;
                setHasMore(list.length >= PAGE_SIZE);
                setNotifications((prev) => [...prev, ...list]);
            }
        } catch (error) {
            console.error('Failed to load more notifications', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [token, isLoadingMore, hasMore]);

    const markAsRead = useCallback((id) => {
        const target = notifications.find((n) => n.id === id);
        if (!target || target.isRead) return;
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
        markNotificationsAsRead([id], token).catch((error) => {
            console.error('Failed to sync read status', error);
        });
    }, [token, notifications]);

    const markAsUnread = useCallback((id) => {
        const target = notifications.find((n) => n.id === id);
        if (!target || !target.isRead) return;
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
        setUnreadCount((prev) => prev + 1);
        markNotificationsAsUnread([id], token).catch((error) => {
            console.error('Failed to sync unread status', error);
        });
    }, [token, notifications]);

    const markAllAsRead = useCallback(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
        markAllNotificationsAsRead(token).catch((error) => {
            console.error('Failed to sync mark-all-as-read', error);
        });
    }, [token]);

    const deleteNotification = useCallback(async (id) => {
        try {
            const response = await deleteNotificationsApi([id], token);
            if (!response?.is_success) {
                toast.error('Failed to delete notification. Please try again.');
                return;
            }
            const target = notifications.find((n) => n.id === id);
            setNotifications((prev) => prev.filter((n) => n.id !== id));
            if (target && !target.isRead) {
                setUnreadCount((prev) => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Failed to delete notification', error);
            toast.error('Failed to delete notification. Please try again.');
        }
    }, [token, notifications]);

    return {
        notifications,
        unreadCount,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        markAsRead,
        markAsUnread,
        markAllAsRead,
        deleteNotification,
    };
}
